/**
 * useCurrentUserName
 * Resolves the logged-in user's display name + initials from:
 *  1. getUserInfo() — first_name / last_name / name / full_name
 *  2. email / username fallback (formatted to Title Case)
 *  3. agent_employee_list API match (when getUserInfo has no name)
 *
 * Shared across: Header, Sidebar, AdminSidebar, CustomerSidebar
 */
import { useMemo, useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ServiceDeskAPI, agentEmployeeListQueryKey } from '@sd/services/APIs';
import { getUserInfo, getAccessToken } from '@/lib/storeManager';
import { jwtDecode } from 'jwt-decode';

/** Converts any string to Title Case, splitting on space / dot / underscore */
function toTitleCase(str: string): string {
  return str
    .replace(/[._]/g, ' ')           // john.doe → john doe
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, c => c.toUpperCase()); // john doe → John Doe
}

/** Strip tenant suffix from username (e.g. krishnac@acme.com_PTS → krishnac@acme.com) */
function stripTenantSuffix(username: string): string {
  return username.replace(/_[A-Z0-9]+$/, '');
}

/** Extract best-effort display name from raw name string */
function formatName(raw: string): string | null {
  const clean = raw.trim();
  if (!clean || clean === ' ') return null;
  return toTitleCase(clean);
}

/** Get display name from email address */
function nameFromEmail(email: string): string | null {
  const local = email.split('@')[0];
  if (!local) return null;
  return toTitleCase(local);
}

export interface UseCurrentUserNameResult {
  displayName: string | null;
  initials: string;
}

export function useCurrentUserName(): UseCurrentUserNameResult {
  // Reactive snapshot: increments after mount so memos re-run once localStorage
  // is fully populated (e.g. right after login, before any page refresh).
  const [snapshot, setSnapshot] = useState(0);
  useEffect(() => {
    setSnapshot(s => s + 1);
    // Also re-read on storage events (cross-tab login/logout)
    const handler = () => setSnapshot(s => s + 1);
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // Step 1: resolve user ID
  const currentUserId = useMemo(() => {
    const info = getUserInfo() as Record<string, unknown> | null;
    if (info?.id) return String(info.id);
    if (info?.user_id) return String(info.user_id);
    try {
      const token = getAccessToken();
      if (!token) return null;
      const p = jwtDecode<Record<string, unknown>>(token);
      const uid = p.user_id ?? p.userId;
      return typeof uid === 'string' ? uid : null;
    } catch { return null; }
  }, [snapshot]);

  // Step 2: try to resolve name from getUserInfo() directly
  const nameFromUserInfo = useMemo(() => {
    const info = getUserInfo() as Record<string, unknown> | null;
    if (!info) return null;

    // Real name fields
    const fn = String(info.first_name ?? '').trim();
    const ln = String(info.last_name ?? '').trim();
    const combined = [fn, ln].filter(Boolean).join(' ');
    const direct = String(info.name ?? info.full_name ?? info.display_name ?? '').trim();
    const fromFields = formatName(direct) ?? (combined ? toTitleCase(combined) : null);
    if (fromFields) return fromFields;

    // Email fallback
    const email = String(info.email ?? '').trim();
    if (email) return nameFromEmail(email);

    // Username fallback (strip tenant suffix then email-prefix)
    const uname = String(info.username ?? '').trim();
    if (uname) {
      const cleaned = stripTenantSuffix(uname);
      return nameFromEmail(cleaned.includes('@') ? cleaned : cleaned);
    }

    return null;
  }, [snapshot]);

  // Step 3: only hit agent_employee_list if step 2 yielded nothing
  const { data: agentListRaw } = useQuery({
    queryKey: agentEmployeeListQueryKey,
    queryFn: () => ServiceDeskAPI.agentEmployees.list(),
    staleTime: 5 * 60 * 1000,
    enabled: !nameFromUserInfo,
  });

  const agentListName = useMemo(() => {
    if (nameFromUserInfo) return null;
    const raw = agentListRaw?.data as unknown;
    let list: unknown[] = [];
    if (Array.isArray(raw)) {
      list = raw;
    } else if (raw && typeof raw === 'object') {
      const o = raw as Record<string, unknown>;
      for (const k of ['results', 'data', 'employees', 'items', 'records', 'rows', 'grid_data']) {
        if (Array.isArray(o[k])) { list = o[k] as unknown[]; break; }
      }
    }
    if (!currentUserId || list.length === 0) return null;

    const userEmail = String((getUserInfo() as any)?.email ?? '').trim().toLowerCase();
    const me = list.find((r: any) =>
      String(r.user_id ?? '') === currentUserId ||
      String(r.id ?? '') === currentUserId ||
      String(r.user?.id ?? '') === currentUserId ||
      String(r.employee_id ?? '') === currentUserId ||
      (userEmail && String(r.email ?? r.user?.email ?? '').toLowerCase() === userEmail)
    ) as any;
    if (!me) return null;

    const fn = String(me.first_name ?? me.user?.first_name ?? '').trim();
    const ln = String(me.last_name ?? me.user?.last_name ?? '').trim();
    const combined = [fn, ln].filter(Boolean).join(' ');
    const direct = String(me.name ?? me.full_name ?? me.display_name ?? '').trim();
    const fromFields = formatName(direct) ?? (combined ? toTitleCase(combined) : null);
    if (fromFields) return fromFields;

    const email = String(me.email ?? me.user?.email ?? '').trim();
    return email ? nameFromEmail(email) : null;
  }, [agentListRaw, currentUserId, nameFromUserInfo, snapshot]);

  const displayName = nameFromUserInfo ?? agentListName;

  const initials = displayName
    ? displayName.split(' ').filter(Boolean).map(s => s[0]).slice(0, 2).join('').toUpperCase()
    : 'U';

  return { displayName, initials };
}
